/**
 * iter76d — Admin Command Palette (Cmd+K / Ctrl+K).
 *
 * Global fuzzy-search that jumps to any tab or opens any product,
 * order, or invoice by typing. Modelled after Raycast / Linear / VS
 * Code palettes — press Cmd+K anywhere in the admin, type a few
 * letters, arrow-key to a result, hit Enter.
 *
 * Data model
 * ----------
 * The palette pre-loads three lists on first open (products, orders,
 * invoices) and caches them for 90 seconds. Static tab entries are
 * always present. Results are ranked as follows:
 *   1. Exact prefix match on primary field (name / order-id / number)
 *   2. Substring match anywhere in primary field
 *   3. Substring match in secondary fields (SKU, customer email, etc.)
 *
 * Keyboard
 * --------
 *   Cmd+K / Ctrl+K  toggle palette
 *   ↑ / ↓           navigate results
 *   Enter           activate (jump to tab, or open detail page)
 *   Esc             close
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Search, Layers, ShoppingBag, FileText, Package, X } from "lucide-react";
import { listProducts, adminListOrders, adminListInvoices } from "../lib/api";

// Static admin destinations. Kept in sync with pages/Admin.jsx `TABS`.
const TAB_ENTRIES = [
  { kind: "tab", id: "dashboard", label: "Dashboard", hint: "Overview + reply-quickly" },
  { kind: "tab", id: "orders", label: "Orders", hint: "Order list + fulfillment" },
  { kind: "tab", id: "production", label: "Production", hint: "In-production board" },
  { kind: "tab", id: "products", label: "Products", hint: "Custom Products + Ready-Made" },
  { kind: "tab", id: "discounts", label: "Discounts", hint: "Coupon codes + site sale" },
  { kind: "tab", id: "reviews", label: "Reviews", hint: "Approve / reject reviews" },
  { kind: "tab", id: "designs", label: "Designs", hint: "Saved customer designs" },
  { kind: "tab", id: "invoices", label: "Invoices", hint: "Invoice list + settings" },
  { kind: "tab", id: "quotes", label: "Quote Requests", hint: "Bulk-order quotes" },
  { kind: "tab", id: "shared-carts", label: "Shared Carts", hint: "Customer share links" },
  { kind: "tab", id: "marketing", label: "Marketing & Ops", hint: "Blasts, newsletter, pixel, etc." },
  { kind: "tab", id: "emails", label: "Emails", hint: "Calendar + Health" },
  { kind: "tab", id: "health", label: "Health · Verification", hint: "Feeds, webhooks, audit, spec scraper" },
  { kind: "tab", id: "audit", label: "Audit Log", hint: "Admin action history" },
  { kind: "tab", id: "traffic", label: "Traffic", hint: "Page-view analytics" },
];

const ICON_FOR = {
  tab: Layers, product: Package, order: ShoppingBag, invoice: FileText,
};

// Rank a single item against the query — higher is better.
function scoreItem(item, q) {
  if (!q) return 0;
  const s = q.toLowerCase();
  const primary = (item.label || "").toLowerCase();
  const secondary = (item.searchBlob || "").toLowerCase();
  if (primary === s) return 1000;
  if (primary.startsWith(s)) return 500 + Math.max(0, 20 - primary.length);
  if (primary.includes(s)) return 200;
  if (secondary.includes(s)) return 50;
  return 0;
}

export default function AdminCommandPalette({ pw, onJump }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(0);
  const [products, setProducts] = useState(null);
  const [orders, setOrders] = useState(null);
  const [invoices, setInvoices] = useState(null);
  const cacheRef = useRef({ at: 0 });
  const inputRef = useRef(null);

  // Global Cmd+K / Ctrl+K toggle
  useEffect(() => {
    const onKey = (e) => {
      const mod = e.metaKey || e.ctrlKey;
      if (mod && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setOpen((x) => !x);
      } else if (e.key === "Escape" && open) {
        setOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  // Load caches on first open (with 90s TTL)
  const refresh = useCallback(async () => {
    const stale = Date.now() - cacheRef.current.at > 90_000;
    if (!stale && products) return;
    cacheRef.current.at = Date.now();
    try {
      const [p, o, i] = await Promise.all([
        listProducts({ limit: 200 }).catch(() => []),
        adminListOrders(pw).catch(() => []),
        adminListInvoices(pw, { limit: 200 }).catch(() => ({ items: [] })),
      ]);
      setProducts(Array.isArray(p) ? p : (p?.items || []));
      setOrders(Array.isArray(o) ? o : (o?.orders || o?.items || []));
      setInvoices(Array.isArray(i) ? i : (i?.items || []));
    } catch (_e) { /* ignore — palette still works with tabs */ }
  }, [pw, products]);

  useEffect(() => { if (open) { refresh(); setTimeout(() => inputRef.current?.focus(), 30); } }, [open, refresh]);

  // Normalize records to a common shape { id, label, sub, kind, searchBlob, onSelect }
  const entries = useMemo(() => {
    const list = [...TAB_ENTRIES];
    (products || []).forEach((p) => {
      list.push({
        kind: "product",
        id: p.id,
        label: p.name || p.slug || p.id,
        hint: `${p.category || "product"} · ${p.brand || ""} ${p.model || p.sku || ""}`.trim(),
        searchBlob: `${p.name} ${p.slug} ${p.sku} ${p.brand} ${p.model} ${p.category}`,
        href: p.category === "ready-made" ? `/ready-made/${p.slug}` : `/customize/${p.slug || p.id}`,
      });
    });
    (orders || []).forEach((o) => {
      const customer = o.customer_email || o.customer_name || o.email || "";
      list.push({
        kind: "order",
        id: o.id || o.order_id,
        label: o.order_number || o.number || o.id,
        hint: `${o.status || "order"} · ${customer} · $${(o.total || 0).toFixed?.(2) ?? o.total}`,
        searchBlob: `${o.order_number} ${o.number} ${o.id} ${customer} ${o.status}`,
        // Orders open in the admin Orders tab; we just jump the tab and rely on operator to find the row.
        tabTarget: "orders",
      });
    });
    (invoices || []).forEach((iv) => {
      list.push({
        kind: "invoice",
        id: iv.id || iv._id,
        label: iv.invoice_number || iv.number || iv.id,
        hint: `${iv.status || "invoice"} · ${iv.customer_email || iv.customer_name || ""} · $${(iv.total || 0).toFixed?.(2) ?? iv.total}`,
        searchBlob: `${iv.invoice_number} ${iv.number} ${iv.id} ${iv.customer_email} ${iv.customer_name} ${iv.status}`,
        tabTarget: "invoices",
      });
    });
    return list;
  }, [products, orders, invoices]);

  // Filter + rank
  const results = useMemo(() => {
    if (!query.trim()) {
      // Empty query — show static tabs as suggestions
      return TAB_ENTRIES.slice(0, 12);
    }
    const scored = entries
      .map((e) => ({ ...e, _score: scoreItem(e, query) }))
      .filter((e) => e._score > 0)
      .sort((a, b) => b._score - a._score)
      .slice(0, 30);
    return scored;
  }, [entries, query]);

  useEffect(() => { setSelected(0); }, [query]);

  const activate = useCallback((item) => {
    if (!item) return;
    setOpen(false);
    setQuery("");
    if (item.kind === "tab") {
      onJump(item.id);
    } else if (item.href) {
      // Open product detail page in a new tab so admin state isn't lost
      window.open(item.href, "_blank", "noopener");
    } else if (item.tabTarget) {
      onJump(item.tabTarget);
    }
  }, [onJump]);

  const onInputKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setSelected((s) => Math.min(results.length - 1, s + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setSelected((s) => Math.max(0, s - 1)); }
    else if (e.key === "Enter") { e.preventDefault(); activate(results[selected]); }
    else if (e.key === "Escape") { setOpen(false); }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-sm flex items-start justify-center pt-[10vh] px-4"
      onClick={() => setOpen(false)}
      data-testid="admin-cmdk-overlay"
    >
      <div
        className="w-full max-w-2xl bg-[#0a0a0a] border border-[#222] shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="admin-cmdk-panel"
      >
        <div className="flex items-center gap-3 border-b border-[#222] px-4 py-3">
          <Search size={18} className="text-zinc-500 shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onInputKey}
            placeholder="Jump to tab · search products, orders, invoices…"
            data-testid="admin-cmdk-input"
            className="flex-1 bg-transparent outline-none text-white font-mono text-sm placeholder:text-zinc-600"
          />
          <button
            onClick={() => setOpen(false)}
            data-testid="admin-cmdk-close"
            className="text-zinc-500 hover:text-white"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>
        <div className="max-h-[60vh] overflow-y-auto py-1" data-testid="admin-cmdk-results">
          {results.length === 0 && (
            <div className="px-4 py-6 text-center font-mono text-xs text-zinc-500">
              No matches for &ldquo;{query}&rdquo;
            </div>
          )}
          {results.map((r, ix) => {
            const Icon = ICON_FOR[r.kind] || Layers;
            const active = ix === selected;
            return (
              <button
                key={`${r.kind}-${r.id}-${ix}`}
                type="button"
                onClick={() => activate(r)}
                onMouseEnter={() => setSelected(ix)}
                data-testid={`admin-cmdk-result-${r.kind}-${r.id}`}
                className={`w-full text-left px-4 py-2.5 flex items-center gap-3 ${
                  active ? "bg-[var(--tc-red)]/20 border-l-2 border-[var(--tc-red)]" : "border-l-2 border-transparent hover:bg-white/5"
                }`}
              >
                <Icon size={14} className={active ? "text-[var(--tc-red)]" : "text-zinc-500"} />
                <div className="flex-1 min-w-0">
                  <div className="text-white text-sm truncate">{r.label}</div>
                  {r.hint && (
                    <div className="font-mono text-[10px] text-zinc-500 truncate">{r.hint}</div>
                  )}
                </div>
                <span className={`font-mono text-[9px] tracking-widest ${
                  r.kind === "tab" ? "text-zinc-500"
                  : r.kind === "product" ? "text-amber-400"
                  : r.kind === "order" ? "text-emerald-400"
                  : "text-cyan-400"
                }`}>
                  {r.kind.toUpperCase()}
                </span>
              </button>
            );
          })}
        </div>
        <div className="border-t border-[#222] px-4 py-2 flex items-center justify-between font-mono text-[10px] text-zinc-500">
          <span>
            <kbd className="border border-[#333] px-1 py-0.5">↑</kbd>
            <kbd className="border border-[#333] px-1 py-0.5 ml-1">↓</kbd> navigate
            {" · "}
            <kbd className="border border-[#333] px-1 py-0.5">↵</kbd> open
            {" · "}
            <kbd className="border border-[#333] px-1 py-0.5">esc</kbd> close
          </span>
          <span>{results.length} result{results.length === 1 ? "" : "s"}</span>
        </div>
      </div>
    </div>
  );
}
